import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { FiX, FiChevronLeft, FiChevronRight, FiLogIn, FiLogOut, FiPhone, FiInfo, FiBarChart3 } = FiIcons;

const Gallery = () => {
  const { user, logout } = useAuth();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'office', name: 'Our Office' },
    { id: 'equipment', name: 'Equipment' },
    { id: 'team', name: 'Our Team' },
    { id: 'treatments', name: 'Treatments' }
  ];

  const galleryItems = [
    {
      id: 1,
      src: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&h=400&fit=crop',
      alt: 'Modern dental office reception',
      category: 'office',
      title: 'Reception Area'
    },
    {
      id: 2,
      src: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=600&h=400&fit=crop',
      alt: 'Dental treatment room',
      category: 'office',
      title: 'Treatment Room'
    },
    {
      id: 3,
      src: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600&h=400&fit=crop',
      alt: 'Advanced dental equipment',
      category: 'equipment',
      title: 'Digital X-Ray Machine'
    },
    {
      id: 4,
      src: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600&h=400&fit=crop',
      alt: 'Dr. Ruby in clinic',
      category: 'team',
      title: 'Dr. Ruby'
    },
    {
      id: 5,
      src: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600&h=400&fit=crop',
      alt: 'Dr. Amna with patient',
      category: 'team',
      title: 'Dr. Amna'
    },
    {
      id: 6,
      src: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&h=400&fit=crop',
      alt: 'Dental cleaning procedure',
      category: 'treatments',
      title: 'Professional Cleaning'
    },
    {
      id: 7,
      src: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=600&h=400&fit=crop',
      alt: 'Orthodontic treatment',
      category: 'treatments',
      title: 'Orthodontic Care'
    },
    {
      id: 8,
      src: 'https://images.unsplash.com/photo-1585435557343-3b092031c4f0?w=600&h=400&fit=crop',
      alt: 'Sterilization equipment',
      category: 'equipment',
      title: 'Sterilization Center'
    },
    {
      id: 9,
      src: 'https://images.unsplash.com/photo-1594824388862-a903b23a0a93?w=600&h=400&fit=crop',
      alt: 'Dr. Nisha consultation',
      category: 'team',
      title: 'Dr. Nisha'
    },
    {
      id: 10,
      src: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=600&h=400&fit=crop',
      alt: 'Comfortable waiting area',
      category: 'office',
      title: 'Waiting Area'
    },
    {
      id: 11,
      src: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=600&h=400&fit=crop',
      alt: 'Dr. Ansad performing surgery',
      category: 'team',
      title: 'Dr. Ansad'
    },
    {
      id: 12,
      src: 'https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?w=600&h=400&fit=crop',
      alt: 'Cosmetic dentistry result',
      category: 'treatments',
      title: 'Cosmetic Dentistry'
    }
  ];

  const filteredItems = selectedCategory === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === selectedCategory);

  const openLightbox = (item) => {
    setSelectedImage(item);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredItems.findIndex(item => item.id === selectedImage.id);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex === filteredItems.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? filteredItems.length - 1 : currentIndex - 1;
    }

    setSelectedImage(filteredItems[newIndex]);
  };

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-maroon-600 to-maroon-800 text-white relative">
        {/* Navigation Tabs */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center space-x-2">
            {/* Dashboard button - only show if user is logged in */}
            {user && (
              <Link
                to="/dashboard"
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
              >
                <SafeIcon icon={FiBarChart3} className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
            )}
            
            {/* About Us tab */}
            <Link
              to="/about"
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>About Us</span>
            </Link>
            
            {/* Contact Us tab */}
            <Link
              to="/contact"
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-full hover:bg-opacity-30 transition-colors flex items-center space-x-2 border border-white border-opacity-30"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              <span>Contact Us</span>
            </Link>
            
            {/* Login/Logout button */}
            {user ? (
              <button
                onClick={logout}
                className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>Logout</span>
              </button>
            ) : (
              <Link
                to="/login"
                className="bg-white text-maroon-600 px-4 py-2 rounded-full hover:bg-grey-100 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiLogIn} className="w-4 h-4" />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Gallery
            </h1>
            <p className="text-xl md:text-2xl text-maroon-100 max-w-3xl mx-auto">
              Take a look inside EasyAI Dental Center and see our state-of-the-art facilities and dedicated team.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-maroon-600 text-white'
                    : 'bg-white text-grey-700 hover:bg-grey-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => openLightbox(item)}
              >
                <div className="relative overflow-hidden rounded-lg shadow-lg bg-white">
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />

                {/* Close Button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>

                {/* Navigation Buttons */}
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
                </button>

                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
                >
                  <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
                </button>

                {/* Image Title */}
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <h3 className="text-white text-xl font-semibold bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                    {selectedImage.title}
                  </h3>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Gallery;